import { useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * Subscribe an admin session to /api/ws/admin and call `onEvent({type, data})`
 * for every server-pushed payload (task_created, task_updated, etc.).
 *
 * Uses the short-lived WS ticket exchange (/api/auth/ws-ticket) — never reads
 * cookies / localStorage directly. Auto-reconnects with a small backoff.
 */
export default function useAdminFirehose({ enabled = true, onEvent }) {
  const wsRef = useRef(null);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let retryTimer = null;
    let attempts = 0;

    const connect = async () => {
      if (cancelled) return;
      let ticket;
      try {
        const { data } = await api.post("/auth/ws-ticket");
        ticket = data.ticket;
      } catch (err) {
        console.error("useAdminFirehose: ws-ticket request failed", err);
        return;
      }
      if (cancelled || !ticket) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = backendUrl.replace(/^http/, "ws");
      let ws;
      try {
        ws = new WebSocket(`${wsBase}/api/ws/admin?token=${encodeURIComponent(ticket)}`);
      } catch (err) {
        console.error("useAdminFirehose: WebSocket construction failed", err);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => { attempts = 0; };
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          handlerRef.current?.(payload);
        } catch (err) {
          console.warn("useAdminFirehose: dropped non-JSON frame", err);
        }
      };
      ws.onclose = () => {
        if (cancelled) return;
        attempts += 1;
        const delay = Math.min(15000, 1000 * 2 ** Math.min(attempts, 4));
        retryTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      const current = wsRef.current;
      if (current) {
        try { current.close(); } catch { /* already closed */ }
      }
      wsRef.current = null;
    };
  }, [enabled]);

  return wsRef;
}
